import { api } from '@/lib/api.ts';
import { getSupabase } from '@/lib/supabase.ts';
import { useCallback, useEffect, useRef, useState } from 'react';

export type SeatmapState = {
  reservedSeatIds: string[];
  soldSeatIds: string[];
  heldSeatIds: string[];
  ownHeldSeatIds: string[];
  ownHoldExpiresAt: string | null;
  ts: string;
};

// When realtime is wired, the channel does the heavy lifting; we poll every
// 30s as a safety net. Without realtime we poll every 3s — the state endpoint
// is cheap (~0.2s) and this keeps seat state feeling live.
const REFRESH_MS_REALTIME = 30_000;
const REFRESH_MS_POLLING = 3_000;

export function useSeatmapState(eventId: string, sessionToken: string) {
  const [state, setState] = useState<SeatmapState | null>(null);
  const timer = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api<SeatmapState>(
        `/events/${eventId}/seatmap/state?sessionToken=${encodeURIComponent(sessionToken)}`,
      );
      setState(data);
    } catch {
      // best-effort; FE keeps last good state
    }
  }, [eventId, sessionToken]);

  useEffect(() => {
    refresh();

    const supa = getSupabase();
    const refreshInterval = supa ? REFRESH_MS_REALTIME : REFRESH_MS_POLLING;

    const tick = () => {
      refresh();
      timer.current = window.setTimeout(tick, refreshInterval);
    };
    timer.current = window.setTimeout(tick, refreshInterval);

    if (!supa) {
      return () => {
        if (timer.current != null) window.clearTimeout(timer.current);
      };
    }

    // Realtime: subscribe to the three tables that change seat state. Any
    // change triggers a debounced refresh of the full state snapshot —
    // simpler and more correct than maintaining a diff client-side.
    let debounce: number | null = null;
    const nudge = () => {
      if (debounce != null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        debounce = null;
        refresh();
      }, 250);
    };

    const channel = supa
      .channel('seat-state')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservation_seats' }, nudge)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'seat_holds' }, nudge)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations' }, nudge)
      .subscribe();

    return () => {
      if (timer.current != null) window.clearTimeout(timer.current);
      if (debounce != null) window.clearTimeout(debounce);
      void supa.removeChannel(channel);
    };
  }, [refresh]);

  return { state, refresh };
}
